'use client'
import React, { useMemo, useRef, useState } from 'react'
import NextLink from 'next/link'
import classNames from 'classnames'
import { TypographyV2 as Typography } from 'components/Typography/TypographyV2'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import { ROUTE_EXCEPT_NOFOLLOW, ROUTE_ASSET_EXCEPT } from 'constants/navigation'
import { getProductAndAddDefaultUtmsToLink } from 'utils/queryManipulation'
import Cookies from 'js-cookie'
import { disableClientSideRoutes } from 'constants/experiments'
import { preserveQueryParams, sanitizeUrlParams, moveAnchorRight } from 'utils/shared'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import logger from '@achieve/cx-logger'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'

const LinkWrapper = React.forwardRef(({ children, refAs, ...props }, ref) => {
  if (refAs) {
    props[refAs] = ref
  }

  return (
    <>
      {React.isValidElement(children)
        ? React.cloneElement(children, props)
        : typeof children === 'string'
        ? children
        : null}
    </>
  )
})

LinkWrapper.displayName = 'LinkWrapper'

/**
 * PlainLink — Tailwind-styled replacement for AscendLink (MUI Link).
 * Renders as <a>, <span>, or <div> depending on the `component` prop.
 * Provides hover underline and focus-visible outline matching the original AscendLink styles.
 */
const PlainLink = React.forwardRef(
  ({ component: Component = 'a', underline, children, className, ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={classNames(
          // Transition matching AscendLink's 300ms color transition
          'transition-colors duration-300 ease-in-out',
          // Underline behavior matching AscendLink underline="hover"
          underline === 'hover' && 'no-underline hover:underline',
          underline === 'always' && 'underline',
          underline === 'none' && 'no-underline',
          // Focus-visible ring matching AscendLink's Mui-focusVisible styles
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3300FF] focus-visible:rounded-sm',
          className
        )}
        {...props}
      >
        {children}
      </Component>
    )
  }
)

PlainLink.displayName = 'PlainLink'

/**
 * AchieveLinkNoMui — Drop-in replacement for AchieveLink without MUI/Ascend dependencies.
 *
 * @param {string} href original href string
 * @param {ReactNode} children child components wrapped with AchieveLinkNoMui
 * @param {boolean} withNextLink if true use next/link, default use PlainLink (Tailwind <a>)
 * @param {RefObject} refAs external ref to be used in the link child
 * @returns {ReactElement} returns link wrapped child component with forwarded
 *                         ref, passed props and transformed/memoized href
 *                         with original query params preserved
 */
const AchieveLinkNoMui = ({
  href,
  children,
  withNextLink = false,
  refAs,
  track,
  onClick,
  rel,
  noLink,
  noPreserveParams,
  ...restProps
}) => {
  const trackRefApiResponse = useRef()
  const continuePropagation = useRef()
  const [userHasInteracted, setUserHasInteracted] = useState(false)
  const { waitForNavigation } = useTriggerEvents()

  const { query } = useAppRouter()

  const [trackAsConversion] = useState(false)

  const visitId = useMemo(() => Cookies.get('eh_visit_id'), [])

  const getUtmEnhancedHref = () => {
    let calcHref = href
    if (!noPreserveParams) {
      calcHref = getProductAndAddDefaultUtmsToLink(preserveQueryParams(href, query, visitId))
    }
    calcHref = moveAnchorRight(calcHref)

    const next = sanitizeUrlParams(calcHref)
    return next
  }

  let linkRel = rel ?? null
  const noFollowExceptionRoute = ROUTE_EXCEPT_NOFOLLOW.findIndex((exception) =>
    String(`${href}`).toLowerCase().includes(exception)
  )
  if (
    String(`${href}`).toLowerCase().includes('http') &&
    noFollowExceptionRoute == -1 &&
    !String(`${href}`).toLowerCase().includes(ROUTE_ASSET_EXCEPT)
  ) {
    if (!String(`${linkRel}`).toLowerCase().includes('nofollow')) {
      linkRel = `${rel ? `${rel} ` : ''}nofollow`
    }
    restProps = { ...restProps, target: '_blank' }
  }
  if (disableClientSideRoutes.includes(href)) {
    onClick = (e) => {
      e?.preventDefault()
      const utmEnhancedHref = getUtmEnhancedHref()
      window.location.href = utmEnhancedHref
    }
  }
  let LinkComponent = withNextLink && !noLink ? NextLink : PlainLink
  const nextProps = withNextLink && !noLink ? { passHref: true } : {}

  const handleClick = (alreadyTracked) => {
    if (track && !alreadyTracked) {
      trackRefApiResponse.current.click()
    }

    if (onClick) {
      onClick()
    }
  }

  let linkNoCrawl = false
  if (typeof href === 'string' && href?.substring(0, 15).includes('legal')) {
    linkNoCrawl = true
    LinkComponent = Typography
  }

  let component
  if (linkNoCrawl) {
    component = 'div'
  } else if (href && !noLink) {
    component = 'a'
  } else {
    component = 'span'
  }

  // Extract base URL (without query params) from href
  const baseHref = useMemo(() => {
    if (!href || typeof href !== 'string') {
      return href
    }

    try {
      const isFullUrl = href.startsWith('http://') || href.startsWith('https://')

      if (isFullUrl) {
        const url = new URL(href)
        return `${url.origin}${url.pathname}${url.hash}`
      } else {
        const [pathWithHash] = href.split('?')
        return pathWithHash
      }
    } catch {
      return href
    }
  }, [href])

  let extraProps = {}
  if (noLink || linkNoCrawl) {
    extraProps = {}
  } else {
    if (!userHasInteracted) {
      extraProps = {
        href: baseHref,
      }
    } else {
      extraProps = {
        href: getUtmEnhancedHref(),
      }
    }
  }

  const handleLinkClick = async (e) => {
    const utmEnhancedHref = getUtmEnhancedHref()
    // Check for modifier keys (cmd+click, ctrl+click, shift+click, middle-click)
    const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1
    if (href && href.indexOf('http') !== -1 && !linkNoCrawl) {
      if (e.nativeEvent.isTrusted) {
        e.preventDefault()
        e.stopPropagation()
        await waitForNavigation({
          ...track,
        })
          .catch(() => {
            logger.log('Event timed out')
            logger.log({ track })
          })
          .finally(() => {
            if (process.env.NODE_ENV !== 'test') {
              if (restProps.target === '_blank' || isModifiedClick) {
                window.open(utmEnhancedHref, '_blank')
              } else {
                window.location.href = utmEnhancedHref
              }
            }
          })
      } else {
        handleClick(true)
      }
    } else if (linkNoCrawl) {
      window.open(utmEnhancedHref, '_self')
    } else if (href && !noLink) {
      e.preventDefault()
      if (restProps.target === '_blank' || isModifiedClick) {
        window.open(utmEnhancedHref, '_blank')
      } else {
        window.location.href = utmEnhancedHref
      }
      handleClick()
    } else {
      handleClick()
    }
  }

  const handleAuxClick = (e) => {
    if (e.button === 1) {
      handleLinkClick(e)
    }
  }

  const handleContextMenu = () => {
    setUserHasInteracted(true)
  }

  return (
    <>
      <LinkComponent
        component={component}
        {...extraProps}
        rel={linkRel}
        ref={linkNoCrawl ? null : continuePropagation}
        underline="hover"
        onClick={handleLinkClick}
        onAuxClick={handleAuxClick}
        onContextMenu={handleContextMenu}
        prefetch={false}
        {...restProps}
        {...(linkNoCrawl ? {} : nextProps)}
      >
        {children?.type === 'a' ? children : <LinkWrapper refAs={refAs}>{children}</LinkWrapper>}
      </LinkComponent>
      <TrackingWrapper
        track={{ href: href, ...track }}
        onClick={onClick}
        withNextLink={withNextLink}
        trackAsConversion={trackAsConversion}
        className="hidden!"
      >
        <span ref={trackRefApiResponse}></span>
      </TrackingWrapper>
    </>
  )
}

export { AchieveLinkNoMui }
